import { classNames } from 'primereact/utils';
import {
  DataTableBaseProps,
  DataTableBodyRowContext,
  DataTableContext,
  DataTablePassThroughOptions,
} from 'primereact/datatable';
import { ColumnContext, ColumnProps } from 'primereact/column';

export const PTDataTable: DataTablePassThroughOptions = {
  root: ({ props }: { props: DataTableBaseProps<any> }) => ({
    className: classNames('relative', {
      'flex flex-col h-full': props.scrollable && props.scrollHeight === 'flex',
    }),
  }),
  loadingOverlay: {
    className: classNames(
      'w-full h-full t-0 l-0 bg-gray-100/30',
      'transition duration-100',
      'absolute flex items-center justify-center z-[1000]',
    ),
  },
  loadingIcon: {
    className: 'w-8 h-8',
  },
  wrapper: ({ props }: { props: DataTableBaseProps<any> }) => ({
    className: classNames({
      relative: props.scrollable,
      'flex flex-col grow h-full':
        props.scrollable && props.scrollHeight === 'flex',
    }),
  }),
  header: () => ({
    className: classNames('p-4 w-1/4'),
  }),
  table: {
    className: 'w-full border-spacing-0',
  },
  thead: ({ context }: { context: DataTableContext }) => ({
    className: classNames({
      'top-0 z-[1]': context.scrollable,
    }),
  }),
  tbody: ({
    props,
    context,
  }: {
    props: DataTableBaseProps<any>;
    context: DataTableContext;
  }) => ({
    className: classNames({
      'sticky z-[1]': props.frozenRow && context.scrollable,
    }),
  }),
  tfoot: ({ context }: { context: DataTableContext }) => ({
    className: classNames({
      'bottom-0 z-[1]': context.scrollable,
    }),
  }),
  footer: {
    className: classNames('border-t-0 border-b border-x-0 font-bold'),
  },
  paginator: {
    RPPDropdown: {
      root: {
        className: 'ax-dropdown--light',
      },
    },
  },
  column: {
    headerCell: ({
      context,
      props,
    }: {
      props: ColumnProps;
      context: ColumnContext;
    }) => ({
      className: classNames(
        'text-left !border-0 !h-2 bg-transparent',
        'transition duration-100',
        context.sorted ? 'ax-text-green' : 'ax-text', // Sort
        {
          'sticky z-[1]': props.frozen, // Frozen Columns
          'overflow-hidden space-nowrap hover:border-y relative bg-clip-padding':
            context.resizable, // Resizable
        },
      ),
    }),
    headerContent: {
      className: 'flex items-center',
    },
    bodyCell: ({
      props,
      context,
    }: {
      props: ColumnProps;
      context: ColumnContext;
    }) => ({
      className: classNames(
        '!whitespace-nowrap !overflow-hidden !text-ellipsis',
        {
          'sticky bg-inherit': props && props.frozen, // Frozen Columns
          'border-x border-y': context.showGridlines,
        },
      ),
    }),
    footerCell: ({ context }: { context: ColumnContext }) => ({
      className: classNames(
        'text-left !border-0 !h-2 bg-transparent',
        'transition duration-100',
        context?.size === 'small'
          ? 'p-2'
          : context?.size === 'large'
            ? 'p-5'
            : 'p-4', // Size
        {
          'border-x border-y': context.showGridlines,
        },
      ),
    }),
    sortIcon: ({ context }: { context: ColumnContext }) => ({
      className: classNames(
        'ml-2',
        context.sorted ? 'text-blue-700' : 'text-slate-700',
      ),
    }),
    sortBadge: {
      className: classNames(
        'flex items-center justify-center align-middle',
        'rounded-[50%] w-[1.143rem] leading-[1.143rem] ml-2',
        'text-blue-700 bg-blue-50',
      ),
    },
    columnFilter: {
      className: 'inline-flex items-center ml-auto',
    },
    filterOverlay: {
      className: classNames(
        'bg-white text-gray-700 border-0 rounded-md min-w-[12.5rem]',
      ),
    },
    filterMatchModeDropdown: {
      root: 'min-[0px]:flex mb-2',
    },
    filterRowItems: {
      className: 'm-0 p-0 py-3 list-none',
    },
    filterRowItem: ({ context }: { context: ColumnContext }) => ({
      className: classNames(
        'm-0 py-3 px-5 bg-transparent',
        'transition duration-100',
        context?.highlighted
          ? 'text-blue-700 bg-blue-100'
          : 'text-gray-700 bg-transparent',
      ),
    }),
    filterOperator: {
      className: classNames(
        'px-5 py-3 border-b border-solid border-gray-300 text-slate-700 bg-slate-50 rounded-t-md',
      ),
    },
    filterOperatorDropdown: {
      root: 'min-[0px]:flex',
    },
    filterConstraint: {
      className: 'p-5 border-b border-solid border-gray-300',
    },
    filterAddRule: {
      className: 'py-3 px-5',
    },
    filterAddRuleButton: {
      root: 'justify-center w-full min-[0px]:text-sm',
      label: 'flex-auto grow-0',
      icon: 'mr-2',
    },
    filterRemoveButton: {
      root: 'ml-2',
      label: 'grow-0',
    },
    filterButtonbar: {
      className: 'flex items-center justify-between p-5',
    },
    filterClearButton: {
      root: 'w-auto min-[0px]:text-sm border-blue-500 text-blue-500 px-4 py-3',
    },
    filterApplyButton: {
      root: 'w-auto min-[0px]:text-sm px-4 py-3',
    },
    filterMenuButton: ({ context }: { context: ColumnContext }) => ({
      className: classNames(
        'inline-flex justify-center items-center cursor-pointer no-underline overflow-hidden relative ml-2',
        'w-8 h-8 rounded-[50%]',
        'transition duration-100',
        'hover:text-slate-700 hover:bg-gray-300/20', // Hover
        'focus:outline-0 focus:outline-offset-0 focus:shadow-[0_0_0_0.2rem_rgba(191,219,254,1)]', // Focus
        {
          'bg-blue-50 text-blue-700': context.active,
        },
      ),
    }),
    headerFilterClearButton: ({ context }: { context: ColumnContext }) => ({
      className: classNames(
        'inline-flex justify-center items-center cursor-pointer no-underline overflow-hidden relative',
        'text-left bg-transparent m-0 p-0 border-none select-none ml-2',
        {
          invisible: !context.hidden,
        },
      ),
    }),
    columnResizer: {
      className:
        'block absolute top-0 right-0 m-0 w-2 h-full p-0 cursor-col-resize border border-transparent',
    },
    rowReorderIcon: {
      className: 'cursor-move',
    },
    rowEditorInitButton: {
      className: classNames(
        'inline-flex items-center justify-center overflow-hidden relative',
        'text-left cursor-pointer select-none',
        'w-8 h-8 border-0',
        'transition duration-100',
      ),
    },
    rowEditorSaveButton: {
      className: classNames(
        'inline-flex items-center justify-center overflow-hidden relative',
        'text-left cursor-pointer select-none',
        'w-8 h-8 border-0',
        'transition duration-100',
      ),
    },
    rowEditorCancelButton: {
      className: classNames('hidden'),
    },
  },
  bodyRow: ({ context }: { context: DataTableBodyRowContext }) => ({
    className: classNames(
      context.selected ? 'selected-row' : 'ax-bg-white ax-text',
      'transition duration-100',
      {
        'cursor-pointer': context.selectable,
      },
    ),
  }),
  rowExpansion: {
    className: 'bg-white text-gray-700',
  },
  rowGroupHeader: {
    className: classNames(
      'sticky z-[1]',
      'bg-white text-gray-700',
      'transition duration-100',
    ),
  },
  rowGroupFooter: {
    className: classNames(
      'sticky z-[1]',
      'bg-white text-gray-700',
      'transition duration-100',
    ),
  },
  rowGroupToggler: {
    className: classNames(
      'text-left m-0 p-0 cursor-pointer select-none',
      'inline-flex items-center justify-center overflow-hidden relative',
      'w-8 h-8 text-gray-500 border-0 bg-transparent rounded-[50%]',
      'transition duration-100',
    ),
  },
  rowGroupTogglerIcon: { className: 'inline-block w-4 h-4' },
  resizeHelper: {
    className: 'absolute hidden w-px z-10 bg-blue-500',
  },
};
